import {IconButton} from '@wandb/components';
import {RunState} from '@wandb/weave/components/RunState';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import {get} from 'lodash';
import React, {useMemo, useState} from 'react';
import {useMutation} from 'react-apollo';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Popup} from 'semantic-ui-react';

import DangerModal from '../components/DangerModal';
import {
  STOP_RUN,
  StopRunMutationData,
  StopRunMutationVariables,
} from '../graphql/runs';
import {getRunStateForRun} from '../util/runs';
import {versionGte} from '../util/version';

interface RunStateIndicatorProps {
  run: {
    id: string;
    config: any;
    state: string;
    stopped: boolean;
  };
  readOnly?: boolean;
}

export default function RunStateIndicator(props: RunStateIndicatorProps) {
  const [stopPressed, setStopPressed] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [stopRun] = useMutation<StopRunMutationData, StopRunMutationVariables>(
    STOP_RUN
  );

  const stopRunAvailable = useMemo(() => {
    return isStopRunAvailable(props.run.config);
  }, [props.run.config]);

  // NOTE: it's possible that props.run.stopped is true but that we failed to
  // actually stop the run -- in that case let the user try to stop it again.
  const canBeStopped = !stopPressed && props.run.state === 'running';

  return (
    <TailwindContents>
      <div className="flex gap-4">
        <RunState value={getRunStateForRun(props.run)} />
        {stopRunAvailable && canBeStopped ? (
          <DangerModal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            trigger={
              <Popup
                position="right center"
                size="mini"
                inverted
                trigger={
                  <IconButton
                    aria-label="Stop run"
                    size="sm"
                    variant="secondary"
                    onPress={() => setModalOpen(true)}
                    icon="stop"
                  />
                }>
                {props.run.stopped
                  ? `Retry stop run (this can take several minutes)`
                  : `Stop run`}
              </Popup>
            }
            message="Are you sure you want to STOP this run? This action cannot be undone!"
            yesText="Stop this run"
            yes={() => {
              stopRun({variables: {id: props.run.id}});
              setStopPressed(true);
            }}
          />
        ) : null}
      </div>
    </TailwindContents>
  );
}

// Can only stop a run if the cli_version is >= 0.8.6 and it's not a Jupyter run
// so given a runConfig, check to see if either of those things are true. If the
// runConfig is invalid JSON, return false.
function isStopRunAvailable(runConfig: string): boolean {
  try {
    const parsedConfig = JSON.parse(runConfig);
    const cliVersion = get(parsedConfig, '_wandb.value.cli_version');
    if (cliVersion == null) {
      return false;
    }

    const isJupyterRun =
      get(parsedConfig, '_wandb.value.is_jupyter_run') ?? false;

    return versionGte(cliVersion, '0.8.6') && !isJupyterRun;
  } catch (e) {
    return false;
  }
}
